import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { get, matches, size } from 'lodash';
import { BehaviorSubject, combineLatest, delay, Observable, skip } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { ShapeSelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { getColorOutput } from '../../../../../../../core/shared/utils/shape-color-utils';
import { ShapeState } from '../../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { isShapeVisible$ } from '../../../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { defaultAnnotationColor } from '../../../../../../../cross-team-shared/constants';
import { BrowserControlService } from '../../../../../../../cross-team-shared/misc-services/browser-control.service';
import {
  getDynamicTag,
  isDynamicOutput,
  isSummaryRegex,
} from '../../../../../../../cross-team-shared/misc-services/tag-helper';
import { AnswerOption } from '../../../../../../../team-apis/otterdam/answer-option';
import { VisibilityState } from '../../../../../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../../../../../../team-apis/otterdam/outputs/annotation-output';
import { AbstractOutputWithAnswerOptions } from '../../../../../../../team-apis/otterdam/outputs/output';
import { Project } from '../../../../../../../team-apis/otterdam/project';
import { outputAnnotationInfoStoreToken } from '../../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../../state/root/abstract-annotation-store';
import { TagService } from '../../../../utils/tag.service';
import { ShapeListPanelService } from '../objectless-shape-list/shape-list-panel.service';

@Component({
  selector: 'h3-shape-list-shape',
  templateUrl: './shape-list-shape.component.html',
  styleUrls: ['./shape-list-shape.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/** @deprecated used for old projects without objects */
export class ShapeListShapeComponent
  extends DirectiveSuperclass
  implements OnInit, OnChanges
{
  @Input({ required: true }) shapeIndex!: number;
  @Input() hoverButtonTemplate?: TemplateRef<unknown>;
  @Input() staticButtonTemplate?: TemplateRef<unknown>;
  @Input() memberOfGroup = false;
  @Output() newlySelected = new EventEmitter<void>();

  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );

  maxFrameNumber = this.taskWorkspaceStore.getMaxFrameNumber(
    this.outputInfoStore.state().output.name,
  );
  shapeColor$!: Observable<string>;
  missingRequiredTag$!: Observable<boolean>;
  visibility$!: Observable<VisibilityState>;
  shapeTagsMap = new Map<string, Observable<string>>();
  isShapeSelected$!: Observable<boolean>;
  shapeStore!: Store<ShapeState>;
  isPanelExpanded$!: Observable<boolean>;
  isPanelAnimationFinished$ = new BehaviorSubject(true);
  hasFeedback$!: Observable<boolean>;

  private isCurrentOutput$ = this.taskWorkspaceStore(
    'selectedOutputName',
  ).$.pipe(
    map(
      (selectedOutputName) =>
        selectedOutputName === this.outputInfoStore.state().output.name,
    ),
    filter((isCurrentOutput) => isCurrentOutput),
  );

  constructor(
    private browserControlService: BrowserControlService,
    private store: AbstractAnnotationStore,
    private tagService: TagService,
    private selectionService: ShapeSelectionService,
    private shapeSelectionService: ShapeSelectionService,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AnnotationOutput>>,
    private shapeListPanelService: ShapeListPanelService,
    private elementRef: ElementRef,
    private taskWorkspaceStore: TaskWorkspaceStore,
  ) {
    super();
  }

  ngOnInit() {
    // @ts-ignore -- fix me
    this.isPanelExpanded$ = this.getInput$('shapeIndex').pipe(
      switchMap((shapeIndex) =>
        this.shapeListPanelService.isShapePanelExpanded$(shapeIndex),
      ),
    );
    this.subscribeTo(this.isPanelExpanded$.pipe(skip(1)), () =>
      this.isPanelAnimationFinished$.next(false),
    );
    this.setUpScrollIntoViewSubscription();
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    this.shapeStore = this.shapesStore(this.shapeIndex);
    if (this.taskWorkspaceStore.isUsingObjects()) {
      this.shapeColor$ = this.getShapeColor$();
    } else {
      this.shapeColor$ = this.tagService.getShapeColor$(this.shapeStore);
    }
    this.missingRequiredTag$ = this.shapeStore.$.pipe(
      filter(isDefined), // `undefined` as the shape is being deleted
      map((shape) => this.tagService.isShapeMissingRequiredTag(shape)),
    );
    this.visibility$ = isShapeVisible$(
      this.store('frameNumber'),
      this.shapeStore,
    );
    this.getShapeTags();
    this.setSubscriptionToSelection();
    this.hasFeedback$ = this.taskWorkspaceStore.shapeHasFeedback$(
      this.shapeIndex,
    );
  }

  getShapeColor$() {
    const output = this.outputInfoStore.state().output;
    const project = this.taskWorkspaceStore.state().project;
    return combineLatest([this.shapeStore.$, this.store('frameNumber').$]).pipe(
      map(([shape, frameNumber]) =>
        getShapeColorObjectless(shape, output, project, frameNumber),
      ),
    );
  }

  modifyShapeSelection(shape: ShapeState, event: MouseEvent) {
    if (event.defaultPrevented) {
      return;
    }

    if (event[this.browserControlService.getPlatformModifier()]) {
      this.selectionService.toggleSelection([shape.index], {
        source: 'shape_list_shape',
      });
    } else {
      this.newlySelected.emit();
      this.selectionService.setSelection([shape.index], {
        source: 'shape_list_shape',
      });
    }
  }

  afterCollapse() {
    this.shapeListPanelService.setShapePanelState(this.shapeIndex, false);
    this.isPanelAnimationFinished$.next(true);
  }

  afterExpand() {
    this.shapeListPanelService.setShapePanelState(this.shapeIndex, true);
    this.isPanelAnimationFinished$.next(true);
  }

  getShapeTags() {
    const answerOptions = this.outputInfoStore.state().output.answer_options;
    this.tagService.getSummaryOutputsForDisplay().forEach((output) => {
      const label =
        answerOptions
          .find((o) => o.name === output.name)!
          .label?.replace(isSummaryRegex, '') || '';
      if (label) {
        this.shapeTagsMap.set(
          label,
          this.tagService.getLabel$(output, this.shapeStore),
        );
      }
    });
  }

  asIsOrder() {
    return 1;
  }

  private setSubscriptionToSelection() {
    this.isShapeSelected$ = combineLatest([
      this.shapeSelectionService.getSelectionForOutput$(),
      this.getInput$('shapeIndex'),
      // @ts-ignore -- fix me
    ]).pipe(map(([selection, index]) => (index ? !!selection[index] : false)));
  }

  private setUpScrollIntoViewSubscription() {
    const revealShape$ = combineLatest([
      this.shapeSelectionService.getSelectionForOutput$(),
      this.isCurrentOutput$,
      this.getInput$('shapeIndex'),
    ]).pipe(
      filter(
        // @ts-ignore -- fix me
        ([currentSelection, isCurrentOutput, shapeIndex]) =>
          size(currentSelection) === 1 &&
          // null check covers an edge case where undoing a shape triggers this
          // pipeline before the component is destroyed
          // fix me if you change this code

          // @ts-ignore -- fix me
          currentSelection[shapeIndex] &&
          isCurrentOutput,
      ),
      switchMap(() =>
        this.isPanelAnimationFinished$.pipe(
          filter((finished) => finished),
          take(1),
        ),
      ),
      // adding a delay of 0 allows the browser to have time to switch tab
      delay(0),
    );

    this.subscribeTo(revealShape$, () =>
      (this.elementRef.nativeElement as Element).scrollIntoView({
        block: 'nearest',
      }),
    );
  }
}

/** @deprecated used for old projects without objects */
function getShapeColorObjectless(
  shape: ShapeState,
  output: AnnotationOutput,
  project: Project,
  frameNumber: number,
) {
  if (shape.transparent) {
    return defaultAnnotationColor;
  }

  const colorOutput = getColorOutput(project, output);
  if (!colorOutput) {
    return defaultAnnotationColor;
  }

  const tag = getColorOutputTag(
    colorOutput,
    shape,
    frameNumber,
    output.answer_options,
  );
  const colorOptions = colorOutput.answer_options;
  const color = get(colorOptions.find(matches({ name: tag })), ['color']);
  return color ?? defaultAnnotationColor;
}

/** @deprecated used for old projects without objects */
function getColorOutputTag(
  colorOutput: AbstractOutputWithAnswerOptions,
  shape: ShapeState,
  frameNumber: number,
  answerOptions: AnswerOption[],
) {
  if (isDynamicOutput(colorOutput.name, answerOptions)) {
    return getDynamicTag(shape.dynamic_tags[colorOutput.name], frameNumber);
  } else {
    return shape.tags[colorOutput.name];
  }
}
